/**
 * Input-format presets for application text questions.
 *
 * Kept in sync (manually) with backend/app/question_formats.py. Patterns are
 * stored on the question without anchors and matched against the whole value.
 * The admin editor resolves a preset choice down to the raw pattern + hint
 * before saving, so applicant/server never need to know which preset was picked.
 */
export interface QuestionFormat {
  key: string;
  label: string;
  pattern: string;
  hint: string;
}

export const QUESTION_FORMATS: QuestionFormat[] = [
  { key: 'mm_yyyy',    label: 'MM/YYYY',     pattern: '(0[1-9]|1[0-2])/\\d{4}',                          hint: 'e.g. 03/2019' },
  { key: 'mm_dd_yyyy', label: 'MM/DD/YYYY',  pattern: '(0[1-9]|1[0-2])/(0[1-9]|[12]\\d|3[01])/\\d{4}',  hint: 'e.g. 03/15/2019' },
  { key: 'email',      label: 'Email',       pattern: '[^@\\s]+@[^@\\s]+\\.[^@\\s]+',                    hint: 'e.g. name@example.com' },
  { key: 'phone_us',   label: 'Phone (US)',  pattern: '\\(?\\d{3}\\)?[-.\\s]?\\d{3}[-.\\s]?\\d{4}',      hint: 'e.g. (555) 123-4567' },
  { key: 'url',        label: 'URL',         pattern: 'https?://[^\\s]+',                                hint: 'e.g. https://example.com' },
  { key: 'digits',     label: 'Digits only', pattern: '\\d+',                                            hint: 'digits only' },
];

/**
 * Loose URL pattern used as the default validation for `link`-type questions.
 * Unlike the strict `url` preset it does not require a scheme, so scheme-less
 * domains like `www.example.com` or `example.com/path` are accepted. It still
 * requires a dot (to reject bare words) and forbids whitespace. Kept in sync with
 * backend/app/question_formats.py (LINK_URL_PATTERN).
 */
export const LINK_URL_PATTERN = '(https?://)?[^\\s]+\\.[^\\s]+';

/** Preset whose pattern matches the given stored pattern, if any. */
export function formatForPattern(pattern?: string): QuestionFormat | undefined {
  if (!pattern) return undefined;
  return QUESTION_FORMATS.find(f => f.pattern === pattern);
}

/**
 * True if `value` fully matches `pattern`. Empty values pass (required-ness is
 * enforced separately). A malformed pattern is treated as passing so a bad admin
 * entry never blocks the applicant.
 */
export function validateFormat(pattern: string | undefined, value: string): boolean {
  if (!pattern || !value) return true;
  try {
    return new RegExp(`^(?:${pattern})$`).test(value);
  } catch {
    return true;
  }
}
